import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LogOut, Download, Package, Users, Mail, Bell, Loader2, RefreshCw,
  Tag, Truck, Plus, Trash2, X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getAuthMe, clearAdminToken, getAdminStats, getOrders,
  getDealerInquiries, getContactMessages, getNewsletterSubscribers, downloadCsv,
  getCategories, getAdminSales, createAdminSale, updateAdminSale, endAdminSale, deleteAdminSale, shipOrder,
} from '../api/api';

const TABS = [
  { key: 'orders', label: 'Orders' },
  { key: 'sales', label: 'Sales' },
  { key: 'dealers', label: 'Dealer Inquiries' },
  { key: 'messages', label: 'Messages' },
  { key: 'newsletter', label: 'Subscribers' },
];

const fmtDate = (d) => {
  try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return d; }
};
const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center space-x-4">
    <div className="w-11 h-11 rounded-lg bg-cyan-50 flex items-center justify-center">
      <Icon className="text-cyan-600" size={22} />
    </div>
    <div>
      <p className="text-2xl font-bold text-gray-900">{value ?? '—'}</p>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const colors = {
    paid: 'bg-green-100 text-green-700', pending: 'bg-yellow-100 text-yellow-700',
    new: 'bg-blue-100 text-blue-700', processing: 'bg-purple-100 text-purple-700',
    shipped: 'bg-indigo-100 text-indigo-700', cancelled: 'bg-red-100 text-red-700',
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-700'}`}>{status || '—'}</span>;
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState('orders');
  const [data, setData] = useState({ orders: [], sales: [], dealers: [], messages: [], newsletter: [] });
  const [loading, setLoading] = useState(false);

  const handleLogout = () => { clearAdminToken(); navigate('/admin/login'); };

  useEffect(() => {
    getAuthMe()
      .then(() => setAuthChecked(true))
      .catch(() => { clearAdminToken(); navigate('/admin/login'); });
  }, [navigate]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, orders, sales, dealers, messages, newsletter] = await Promise.all([
        getAdminStats(), getOrders(), getAdminSales(), getDealerInquiries(), getContactMessages(), getNewsletterSubscribers(),
      ]);
      setStats(s);
      setData({ orders, sales, dealers, messages, newsletter });
    } catch (err) {
      if (err.response?.status === 401) { clearAdminToken(); navigate('/admin/login'); }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (authChecked) loadAll(); }, [authChecked]); // eslint-disable-line

  if (!authChecked) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-cyan-600" size={32} /></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-xs text-gray-500">Rezvani Furniture Studio</p>
          </div>
          <div className="flex items-center space-x-2">
            <button onClick={loadAll} className="px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg flex items-center" data-testid="admin-refresh-btn">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
            <button onClick={handleLogout} className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center" data-testid="admin-logout-btn">
              <LogOut size={16} className="mr-1.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Package} label="Total Orders" value={stats?.totalOrders} />
          <StatCard icon={Users} label="Dealer Inquiries" value={data.dealers.length} />
          <StatCard icon={Mail} label="Messages" value={stats?.totalMessages} />
          <StatCard icon={Bell} label="Subscribers" value={stats?.totalSubscribers} />
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition ${tab === t.key ? 'bg-white text-cyan-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                data-testid={`admin-tab-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {tab === 'orders' && (
            <button onClick={() => downloadCsv('/orders/export/csv', 'orders.csv')} className="px-4 py-2 text-sm bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg flex items-center" data-testid="export-orders-btn">
              <Download size={16} className="mr-1.5" /> Export CSV
            </button>
          )}
          {tab === 'dealers' && (
            <button onClick={() => downloadCsv('/dealer-inquiry/export/csv', 'dealer_inquiries.csv')} className="px-4 py-2 text-sm bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg flex items-center" data-testid="export-dealers-btn">
              <Download size={16} className="mr-1.5" /> Export CSV
            </button>
          )}
        </div>

        {/* Table panel */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-cyan-600" size={28} /></div>
          ) : (
            <div className="overflow-x-auto">
              {tab === 'orders' && <OrdersTable rows={data.orders} onChanged={loadAll} />}
              {tab === 'sales' && <SalesPanel rows={data.sales} onChanged={loadAll} />}
              {tab === 'dealers' && <DealersTable rows={data.dealers} />}
              {tab === 'messages' && <MessagesTable rows={data.messages} />}
              {tab === 'newsletter' && <NewsletterTable rows={data.newsletter} />}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

const Th = ({ children }) => <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">{children}</th>;
const Td = ({ children }) => <td className="px-4 py-3 text-sm text-gray-700 align-top">{children}</td>;
const Empty = ({ msg }) => <div className="py-16 text-center text-gray-400 text-sm">{msg}</div>;

const ShipForm = ({ order, onDone, onCancel }) => {
  const [form, setForm] = useState({ carrier: '', trackingNumber: '', trackingUrl: '' });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await shipOrder(order.id, { ...form, trackingUrl: form.trackingUrl || null });
      toast.success('Shipping email sent', { description: `Customer notified for order #${order.id.slice(0, 8).toUpperCase()}` });
      onDone();
    } catch (err) {
      toast.error('Could not send shipping email', { description: err.response?.data?.detail || 'Please try again.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="flex flex-wrap items-end gap-2 bg-cyan-50 border border-cyan-200 rounded-lg p-3 mt-2" data-testid={`ship-form-${order.id}`}>
      <div>
        <label className="block text-[11px] font-semibold text-gray-600 mb-1">Carrier *</label>
        <input required value={form.carrier} onChange={set('carrier')} placeholder="UPS / FedEx / USPS" data-testid="ship-carrier"
          className="h-9 w-36 px-2.5 rounded-md border border-gray-300 text-sm outline-none focus:border-cyan-500" />
      </div>
      <div>
        <label className="block text-[11px] font-semibold text-gray-600 mb-1">Tracking # *</label>
        <input required value={form.trackingNumber} onChange={set('trackingNumber')} placeholder="1Z999..." data-testid="ship-tracking-number"
          className="h-9 w-44 px-2.5 rounded-md border border-gray-300 text-sm outline-none focus:border-cyan-500" />
      </div>
      <div>
        <label className="block text-[11px] font-semibold text-gray-600 mb-1">Tracking URL (optional)</label>
        <input value={form.trackingUrl} onChange={set('trackingUrl')} placeholder="https://..." data-testid="ship-tracking-url"
          className="h-9 w-52 px-2.5 rounded-md border border-gray-300 text-sm outline-none focus:border-cyan-500" />
      </div>
      <button type="submit" disabled={sending} data-testid="ship-submit"
        className="h-9 px-4 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium rounded-md flex items-center disabled:opacity-60">
        {sending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Truck size={14} className="mr-1.5" />} Send
      </button>
      <button type="button" onClick={onCancel} className="h-9 px-2.5 text-gray-500 hover:text-gray-700"><X size={16} /></button>
    </form>
  );
};

const OrdersTable = ({ rows, onChanged }) => {
  const [shippingId, setShippingId] = useState(null);
  if (!rows.length) return <Empty msg="No orders yet." />;
  return (
    <table className="w-full" data-testid="orders-table">
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr><Th>Date</Th><Th>Order #</Th><Th>Customer</Th><Th>Contact</Th><Th>Items</Th><Th>Total</Th><Th>Status</Th><Th>Shipping</Th></tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.map((o) => {
          const ci = o.customerInfo || {};
          return (
            <tr key={o.id} className="hover:bg-gray-50">
              <Td>{fmtDate(o.createdAt)}</Td>
              <Td><span className="font-mono text-xs font-semibold text-gray-700">#{(o.id || '').slice(0, 8).toUpperCase()}</span></Td>
              <Td>
                <div className="font-medium text-gray-900">{ci.firstName} {ci.lastName}</div>
                <div className="text-xs text-gray-500">{ci.shippingAddress?.city}, {ci.shippingAddress?.state}</div>
              </Td>
              <Td><div className="text-xs">{ci.email}</div><div className="text-xs text-gray-500">{ci.phone}</div></Td>
              <Td><div className="text-xs max-w-xs">{(o.items || []).map((it, i) => <div key={i}>{it.quantity}× {it.name} <span className="text-gray-400">({it.selectedColor})</span></div>)}</div></Td>
              <Td className="font-semibold">{fmtMoney(o.total)}</Td>
              <Td><StatusBadge status={o.status === 'shipped' ? 'shipped' : o.paymentStatus} /></Td>
              <Td>
                {o.shipping ? (
                  <div className="text-xs">
                    <div className="font-medium text-indigo-700 flex items-center"><Truck size={12} className="mr-1" /> {o.shipping.carrier}</div>
                    <div className="text-gray-500">{o.shipping.trackingNumber}</div>
                  </div>
                ) : shippingId === o.id ? (
                  <ShipForm order={o} onCancel={() => setShippingId(null)} onDone={() => { setShippingId(null); onChanged(); }} />
                ) : (
                  <button onClick={() => setShippingId(o.id)} data-testid={`ship-btn-${o.id}`}
                    className="px-3 py-1.5 text-xs font-medium bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-md flex items-center">
                    <Truck size={13} className="mr-1" /> Ship
                  </button>
                )}
              </Td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const EMPTY_SALE_FORM = { percent: 10, label: '', categories: [], endsAt: '', countdownDays: 7 };

const SalesPanel = ({ rows, onChanged }) => {
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(EMPTY_SALE_FORM);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { getCategories().then(setCategories).catch(() => {}); }, []);

  const toggleCategory = (id) => setForm((f) => ({
    ...f,
    categories: f.categories.includes(id) ? f.categories.filter((c) => c !== id) : [...f.categories, id],
  }));

  const startEdit = (s) => {
    setEditingId(s.id);
    setForm({
      percent: s.percent,
      label: s.label,
      categories: s.categories || [],
      endsAt: (s.endsAt || '').slice(0, 10),
      countdownDays: s.countdownDays ?? 7,
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, percent: Number(form.percent), countdownDays: Number(form.countdownDays) };
      if (editingId) {
        await updateAdminSale(editingId, payload);
        toast.success('Sale updated');
      } else {
        await createAdminSale(payload);
        toast.success('Sale started', { description: 'Prices update immediately across the store.' });
      }
      setShowForm(false); setEditingId(null); setForm(EMPTY_SALE_FORM);
      onChanged();
    } catch (err) {
      toast.error('Could not save sale', { description: err.response?.data?.detail || 'Please check the fields.' });
    } finally {
      setSaving(false);
    }
  };

  const endNow = async (s) => {
    try { await endAdminSale(s.id); toast.success('Sale ended', { description: 'Prices reverted immediately.' }); onChanged(); }
    catch { toast.error('Could not end sale'); }
  };
  const remove = async (s) => {
    try { await deleteAdminSale(s.id); toast.success('Sale deleted'); onChanged(); }
    catch { toast.error('Could not delete sale'); }
  };

  const catName = (id) => categories.find((c) => c.id === id)?.name || id;

  return (
    <div className="p-4 sm:p-6" data-testid="sales-panel">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-600">Sales apply instantly storewide — banners &amp; countdown timers appear only during the final countdown window.</p>
        <button
          onClick={() => { setShowForm((s) => !s); setEditingId(null); setForm(EMPTY_SALE_FORM); }}
          data-testid="new-sale-btn"
          className="px-4 py-2 text-sm bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg flex items-center shrink-0"
        >
          {showForm && !editingId ? <X size={16} className="mr-1.5" /> : <Plus size={16} className="mr-1.5" />}
          {showForm && !editingId ? 'Close' : 'New Sale'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-5" data-testid="sale-form">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Discount % *</label>
              <input required type="number" min="1" max="90" value={form.percent} data-testid="sale-percent"
                onChange={(e) => setForm((f) => ({ ...f, percent: e.target.value }))}
                className="h-10 w-full px-3 rounded-lg border border-gray-300 text-sm outline-none focus:border-cyan-500" />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Label *</label>
              <input required value={form.label} placeholder="10% Off Everything" data-testid="sale-label"
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                className="h-10 w-full px-3 rounded-lg border border-gray-300 text-sm outline-none focus:border-cyan-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Ends (end of day AZ) *</label>
              <input required type="date" value={form.endsAt} data-testid="sale-ends"
                onChange={(e) => setForm((f) => ({ ...f, endsAt: e.target.value }))}
                className="h-10 w-full px-3 rounded-lg border border-gray-300 text-sm outline-none focus:border-cyan-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Show timer last N days</label>
              <input type="number" min="0" max="60" value={form.countdownDays} data-testid="sale-countdown-days"
                onChange={(e) => setForm((f) => ({ ...f, countdownDays: e.target.value }))}
                className="h-10 w-full px-3 rounded-lg border border-gray-300 text-sm outline-none focus:border-cyan-500" />
            </div>
          </div>
          <div className="mb-3">
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Applies to (none selected = everything)</label>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button type="button" key={c.id} onClick={() => toggleCategory(c.id)} data-testid={`sale-cat-${c.id}`}
                  className={`px-3 py-1.5 text-xs rounded-full border transition ${form.categories.includes(c.id) ? 'bg-cyan-600 text-white border-cyan-600' : 'bg-white text-gray-600 border-gray-300 hover:border-cyan-400'}`}>
                  {c.name}
                </button>
              ))}
            </div>
          </div>
          <button type="submit" disabled={saving} data-testid="sale-save"
            className="px-5 py-2.5 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium rounded-lg flex items-center disabled:opacity-60">
            {saving && <Loader2 size={14} className="animate-spin mr-1.5" />}
            {editingId ? 'Update Sale' : 'Start Sale'}
          </button>
        </form>
      )}

      {!rows.length ? <Empty msg="No sales yet — start one above." /> : (
        <table className="w-full" data-testid="sales-table">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr><Th>Label</Th><Th>Discount</Th><Th>Scope</Th><Th>Ends</Th><Th>Timer Window</Th><Th>Status</Th><Th>Actions</Th></tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((s) => (
              <tr key={s.id} className="hover:bg-gray-50">
                <Td><div className="font-medium text-gray-900 flex items-center"><Tag size={13} className="mr-1.5 text-cyan-600" />{s.label}</div></Td>
                <Td className="font-semibold">{s.percent}%</Td>
                <Td><div className="text-xs max-w-[180px]">{(s.categories || []).length ? s.categories.map(catName).join(', ') : 'Everything'}</div></Td>
                <Td>{fmtDate(s.endsAt)}</Td>
                <Td>Last {s.countdownDays ?? 7} days</Td>
                <Td><StatusBadge status={s.status === 'active' ? 'paid' : 'cancelled'} />{' '}<span className="text-xs text-gray-500">{s.status}</span></Td>
                <Td>
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => startEdit(s)} data-testid={`sale-edit-${s.id}`}
                      className="px-2.5 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md">Edit</button>
                    {s.status === 'active' && (
                      <button onClick={() => endNow(s)} data-testid={`sale-end-${s.id}`}
                        className="px-2.5 py-1 text-xs bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-md">End now</button>
                    )}
                    <button onClick={() => remove(s)} data-testid={`sale-delete-${s.id}`}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded-md"><Trash2 size={14} /></button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const DealersTable = ({ rows }) => {
  if (!rows.length) return <Empty msg="No dealer inquiries yet." />;
  return (
    <table className="w-full" data-testid="dealers-table">
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr><Th>Date</Th><Th>Company</Th><Th>Contact</Th><Th>Location</Th><Th>Message</Th><Th>Status</Th></tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.map((d) => (
          <tr key={d.id} className="hover:bg-gray-50">
            <Td>{fmtDate(d.createdAt)}</Td>
            <Td className="font-medium text-gray-900">{d.companyName}</Td>
            <Td><div>{d.contactName}</div><div className="text-xs text-gray-500">{d.email}</div><div className="text-xs text-gray-500">{d.phone}</div></Td>
            <Td>{d.location}</Td>
            <Td><div className="text-xs max-w-xs">{d.message}</div></Td>
            <Td><StatusBadge status={d.status} /></Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const MessagesTable = ({ rows }) => {
  if (!rows.length) return <Empty msg="No contact messages yet." />;
  return (
    <table className="w-full" data-testid="messages-table">
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr><Th>Date</Th><Th>Name</Th><Th>Contact</Th><Th>Subject</Th><Th>Message</Th></tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.map((m) => (
          <tr key={m.id} className="hover:bg-gray-50">
            <Td>{fmtDate(m.createdAt)}</Td>
            <Td className="font-medium text-gray-900">{m.name}</Td>
            <Td><div className="text-xs">{m.email}</div><div className="text-xs text-gray-500">{m.phone}</div></Td>
            <Td>{m.subject}</Td>
            <Td><div className="text-xs max-w-md">{m.message}</div></Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const NewsletterTable = ({ rows }) => {
  if (!rows.length) return <Empty msg="No subscribers yet." />;
  return (
    <table className="w-full" data-testid="newsletter-table">
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr><Th>Email</Th><Th>Subscribed</Th><Th>Status</Th></tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.map((n) => (
          <tr key={n.id} className="hover:bg-gray-50">
            <Td className="font-medium text-gray-900">{n.email}</Td>
            <Td>{fmtDate(n.subscribedAt)}</Td>
            <Td><StatusBadge status={n.status} /></Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default AdminDashboard;
